<template>
    <section name="tab-section" class="gap-md flex flex-col">
        <!-- The strip scrolls sideways instead of wrapping (the app's mobile
             convention, and the treatment underline tabs will need too). On
             phones it bleeds through the page's px-xs to the screen edge so a
             clipped pill signals there is more to scroll to. -->
        <div class="gap-xs hide-scrollbar max-md:-mx-xs max-md:px-xs flex overflow-x-auto rounded-sm">
            <button
                v-for="tab in tabs"
                :key="tab.id"
                :class="{
                    'bg-(--bg-negative) font-semibold text-(--text-negative)': activeTab === tab.name,
                    'bg-primary font-matter hover:bg-(--bg-negative) font-light hover:text-(--text-negative)': activeTab !== tab.name,
                }"
                class="gap-sm flex shrink-0 cursor-pointer items-center rounded-sm whitespace-nowrap"
                @click="onTabClick(tab.id, $event)"
            >
                {{ tabLabel(tab) }}
                <p v-if="tab.associationCount" class="text-primary px-xs py-2xs rounded-sm bg-(--surface-1)">
                    {{ tab.associationCount }}
                </p>
            </button>
        </div>

        <p v-if="activeTabDescription" class="gap-xs text-muted flex items-center text-sm">
            <i class="i-info-circle shrink-0" />
            {{ activeTabDescription }}
        </p>

        <template v-for="tab in tabs" :key="tab.id">
            <component
                :is="tab.component"
                v-show="activeTab == tab.name"
                :data="tab.data"
                :loading="tab.loading"
                v-bind="tab.props ?? {}"
                @add="$emit('add', tab.id)"
                @update="$emit('update', tab.id)"
                @remove="$emit('remove', $event)"
            />
        </template>
    </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import type { Tab } from '@/stores/store-interfaces';

const route = useRoute();
const activeTab = computed(() => props.activeTab ?? (route.query.selected as string));
const props = defineProps<{
    tabs: Tab[];
    activeTab?: string;
}>();

const activeTabDescription = computed(() => props.tabs.find((tab) => tab.name === activeTab.value)?.description);

function tabLabel(tab: Tab) {
    return tab.label ?? tab.name.charAt(0).toUpperCase() + tab.name.slice(1);
}

const emit = defineEmits<{
    tabChange: [id: Tab['id']];
    add: [id: Tab['id']];
    update: [id: Tab['id']];
    remove: [payload: unknown];
}>();

function onTabClick(id: Tab['id'], event: MouseEvent) {
    emit('tabChange', id);
    // Keep the picked tab fully visible when it was the clipped one at the
    // edge of the scrollable strip.
    (event.currentTarget as HTMLElement | null)?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
    });
}
</script>
